import { FC, ReactNode } from "react";
import { Box } from "@mantine/core";
import cx from "clsx";
import styles from "@scss/custom-components/content-grid.module.scss";
import { useSidebar } from "@/src/contexts/sidebar-context";

interface WrapperProps {
  children: ReactNode;
  className?: string;
  broadcastVariant?: boolean;
}

export const CustomContentGrid: FC<WrapperProps> & {
  Jumbotron: FC<SectionProps>;
  Main: FC<SectionProps>;
  Side: FC<SectionProps>;
} = ({ children, className, broadcastVariant }) => {
  const { isCollapsed } = useSidebar();

  return (
    <Box
      className={cx(
        styles.gridWrapper,
        {
          [styles.sidebarCollapsed]: isCollapsed,
          [styles.broadcastVariant]: broadcastVariant,
        },
        className,
      )}
    >
      {children}
    </Box>
  );
};

/* ---------- Sub-components ---------- */

interface SectionProps {
  children: ReactNode;
  className?: string;
  broadcastVariant?: boolean;
  mobileBgImage?: string;
  desktopBgImage?: string;
  jumbotronTopLeftImage?: string;
  jumbotronBottomRightImage?: string;
  bgImageOpacity?: number;
  bordered?: boolean;
  mb0?: boolean;
}

const Jumbotron: FC<SectionProps> = ({ 
  children, 
  className, 
  broadcastVariant,
  mobileBgImage,
  desktopBgImage,
  jumbotronTopLeftImage,
  jumbotronBottomRightImage,
  bgImageOpacity
}) => {
  const backgroundImage = desktopBgImage || mobileBgImage;
  
  return (
    <Box
      maw={"100%"}
      className={cx(
        styles.area,
        styles["area--jumbotron"],
        {
          [styles.broadcastVariant]: broadcastVariant,
        },
        className,
      )}
      style={{
        position: 'relative',
        backgroundColor: '#323135'
      }}
    >
      {backgroundImage && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: bgImageOpacity ?? 1,
            zIndex: 0
          }}
        />
      )}
      <Box style={{ position: 'relative', zIndex: 1 }}>
        {jumbotronTopLeftImage && (
          <img
            src={jumbotronTopLeftImage}
            alt=""
            className={styles.jumbotronTopLeft}
          />
        )}
        {children}
        {jumbotronBottomRightImage && (
          <img
            src={jumbotronBottomRightImage}
            alt=""
            className={styles.jumbotronBottomRight}
          />
        )}
      </Box>
    </Box>
  );
};

const Main: FC<SectionProps> = ({ 
  children, 
  className, 
  broadcastVariant,
  mobileBgImage,
  desktopBgImage,
  bgImageOpacity,
  bordered = false,
  mb0 = false
}) => {
  const backgroundImage = desktopBgImage || mobileBgImage;
  
  return (
    <Box
      maw={"100%"}
      mb={mb0 ? 0 : undefined}
      className={cx(
        styles.area,
        styles["area--main"],
        {
          [styles.broadcastVariant]: broadcastVariant,
        },
        className,
      )}
      style={{
        position: 'relative',
        backgroundColor: backgroundImage ? '#323135' : undefined,
      }}
    >
      {backgroundImage && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: bgImageOpacity ?? 1,
            zIndex: 0
          }}
        />
      )}
      {bordered ? (
        <Box className={styles["area--bordered"]} mb={mb0 ? 0 : undefined}>
          {children}
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

const Side: FC<SectionProps> = ({ children, className, broadcastVariant }) => {
  return (
    <Box
      className={cx(
        styles.area,
        styles["area--side"],
        {
          [styles.broadcastVariant]: broadcastVariant,
        },
        className,
      )}
    >
      {children}
    </Box>
  );
};

CustomContentGrid.Jumbotron = Jumbotron;
CustomContentGrid.Main = Main;
CustomContentGrid.Side = Side;